import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Patient } from '@/lib/types';
import { patientService } from '@/services/patientService';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface UpdatePatientStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patient: Patient;
  newStatus: 'active' | 'inactive';
  onSuccess?: () => void;
}

const UpdatePatientStatusDialog: React.FC<UpdatePatientStatusDialogProps> = ({
  open,
  onOpenChange,
  patient,
  newStatus,
  onSuccess,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);

  if (!patient) return null;

  const isActivating = newStatus === 'active';
  const actionText = isActivating ? 'Activate' : 'Deactivate';
  const actionDescription = isActivating
    ? 'This will activate the patient and make them visible in the system.'
    : 'This will deactivate the patient. They will no longer appear in active patient lists, but their data will be preserved.';

  const handleConfirm = async () => {
    if (patient.status === newStatus) {
      toast({
        title: 'No Change',
        description: 'Patient is already in this status',
        variant: 'default',
      });
      onOpenChange(false);
      return;
    }

    setIsUpdating(true);
    try {
      const response = await patientService.updatePatientStatus(patient.id, newStatus);

      if (response.success) {
        toast({
          title: 'Success',
          description: `Patient has been ${isActivating ? 'activated' : 'deactivated'} successfully.`,
          variant: 'success',
        });
        onOpenChange(false);
        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error(response.message || 'Failed to update patient status');
      }
    } catch (error: any) {
      console.error('Error updating patient status:', error);
      toast({
        title: 'Error',
        description: error?.message || 'Failed to update patient status',
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    if (!isUpdating) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <div className="flex items-center space-x-3">
            <div
              className={cn(
                'w-12 h-12 rounded-full flex items-center justify-center',
                isActivating ? 'bg-emerald-500/10' : 'bg-slate-500/10'
              )}
            >
              {isActivating ? (
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              ) : (
                <XCircle className="h-6 w-6 text-slate-600" />
              )}
            </div>
            <div className="flex-1">
              <DialogTitle className="text-[1.05rem] font-semibold">
                {actionText} Patient
              </DialogTitle>
              <DialogDescription className="text-[0.8rem] text-slate-500 mt-0">
                {isActivating ? 'Activate patient access' : 'Deactivate patient access'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-slate-700">
            Are you sure you want to {actionText.toLowerCase()}{' '}
            <span className="font-semibold">{patient.full_name}</span>?
          </p>
          <p className="text-sm text-slate-500 mt-2">{actionDescription}</p>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isUpdating}
            className={cn(
              'gap-2',
              isActivating ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-600 hover:bg-slate-700'
            )}
          >
            {isUpdating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {isActivating ? 'Activating...' : 'Deactivating...'}
              </>
            ) : (
              <>
                {isActivating ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <XCircle className="h-4 w-4" />
                )}
                {actionText}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpdatePatientStatusDialog;

